import React, { useEffect, useRef, useState } from 'react';
import { PrimaryButton, Card, Pill, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';

interface StopClockGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

type Stage = 'idle' | 'calibrating' | 'running' | 'evaluating' | 'result';

const ROUND_MS = 5000;
const WIN_THRESHOLD_MS = 150;
const TARGET_ANGLE_DEG = 270;
/** Visual green arc — matches server GamesConstants.STOP_CLOCK_WIN_THRESHOLD_MS */
const TARGET_ZONE_HALF_WIDTH_DEG = Math.round((WIN_THRESHOLD_MS / ROUND_MS) * 360);

/**
 * The dial is cosmetic only. The server owns the clock:
 * stopClockStart records the start timestamp server-side;
 * stopClockStop computes precision from its own receive time.
 * The client only sends the stop signal and displays the server verdict.
 */
const StopClockGame: React.FC<StopClockGameProps> = ({ appName, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'STOP_CLOCK');
  const [stage, setStage] = useState<Stage>('idle');
  const [needleAngle, setNeedleAngle] = useState(0);
  const [precisionMs, setPrecisionMs] = useState<number | null>(null);
  const [success, setSuccess] = useState(false);
  const [redemptionCode, setRedemptionCode] = useState<string | undefined>();
  const sessionTokenRef = useRef<string>('');
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  async function handleStart() {
    if (!eligible || stage === 'result') return;
    setStage('calibrating');
    try {
      const startRes = await gamesService.stopClockStart(appName);
      sessionTokenRef.current = startRes.sessionToken;
      // Two pings for latency calibration
      await gamesService.stopClockPing(appName, startRes.sessionToken);
      await gamesService.stopClockPing(appName, startRes.sessionToken);
    } catch {
      setStage('idle');
      return;
    }

    setStage('running');
    const startTime = performance.now();
    const tick = () => {
      const elapsed = (performance.now() - startTime) % ROUND_MS;
      setNeedleAngle((elapsed / ROUND_MS) * 360);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  async function handleStop() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setStage('evaluating');
    try {
      const res = await gamesService.stopClockStop(appName, sessionTokenRef.current);
      setPrecisionMs(res.precisionMs);
      setSuccess(res.success);
      setRedemptionCode(res.redemptionCode);
    } catch {
      setPrecisionMs(null);
      setSuccess(false);
    }
    setStage('result');
  }

  const size = 260;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 20;

  function polar(radius: number, angleDeg: number) {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)] as const;
  }

  const ticks = Array.from({ length: 60 }, (_, i) => {
    const ang = i * 6;
    const isMajor = i % 5 === 0;
    const [x0, y0] = polar(r, ang);
    const [x1, y1] = polar(r - (isMajor ? 10 : 5), ang);
    return { x0, y0, x1, y1, w: isMajor ? 2.5 : 1 };
  });

  const [needleX, needleY] = polar(r - 16, needleAngle);
  const targetArcStart = polar(r - 1, TARGET_ANGLE_DEG - TARGET_ZONE_HALF_WIDTH_DEG);
  const targetArcEnd = polar(r - 1, TARGET_ANGLE_DEG + TARGET_ZONE_HALF_WIDTH_DEG);

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={
          stage === 'result' ? (
            success
              ? <>🎯 Perfect <span style={{ color: 'var(--primary-dark, #D4A416)' }}>Timing!</span></>
              : <>So Close — <span style={{ color: '#D9472B' }}>Try Tomorrow</span></>
          ) : (
            <>Stop the <span style={{ color: 'var(--primary-dark, #D4A416)' }}>Clock</span></>
          )
        }
        subtitle={
          stage === 'result'
            ? `You were ${precisionMs}ms from target`
            : 'Tap STOP when the needle hits the green zone'
        }
        onClose={onClose}
      />

      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {!loading && !eligible && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && (
          <>
        {stage !== 'result' && (
          <div style={{ position: 'relative', width: size, height: size, margin: '10px auto 4px' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <circle cx={cx} cy={cy} r={r} fill="#FFFFFF" stroke="var(--border-color, #E5E2D8)" strokeWidth={2} />
              {ticks.map((t, i) => (
                <line key={i} x1={t.x0} y1={t.y0} x2={t.x1} y2={t.y1}
                  stroke="var(--border-strong, #C5B99A)" strokeWidth={t.w} />
              ))}
              <path
                d={`M${targetArcStart[0]},${targetArcStart[1]} A${r - 1},${r - 1} 0 0 1 ${targetArcEnd[0]},${targetArcEnd[1]}`}
                fill="none"
                stroke="#2E7D5B"
                strokeWidth={8}
                strokeLinecap="round"
              />
              <line x1={cx} y1={cy} x2={needleX} y2={needleY}
                stroke="#D9472B" strokeWidth={4} strokeLinecap="round" />
              <circle cx={cx} cy={cy} r={8} fill="var(--primary, #F5C21A)" />
            </svg>
          </div>
        )}

        {stage === 'idle' && <PrimaryButton onClick={handleStart}>START CHALLENGE</PrimaryButton>}
        {stage === 'calibrating' && <PrimaryButton disabled>CALIBRATING…</PrimaryButton>}
        {stage === 'running' && (
          <PrimaryButton variant="ember" onClick={handleStop}>STOP</PrimaryButton>
        )}
        {stage === 'evaluating' && <PrimaryButton disabled>CHECKING…</PrimaryButton>}

        {stage === 'result' && (
          <>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 42 }}>{success ? '✅' : '💛'}</div>
            {success && redemptionCode ? (
              <>
                <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--primary-dark, #D4A416)', marginTop: 8 }}>
                  Free Dessert
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)', marginTop: 2 }}>
                  Precision reward · within {precisionMs}ms
                </p>
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--border-color, #E5E2D8)' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)' }}>CODE</div>
                  <div style={{ fontWeight: 700, fontSize: 18, letterSpacing: 2 }}>{redemptionCode}</div>
                </div>
              </>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-secondary, #6B5E3E)', marginTop: 8 }}>
                You missed the target zone{precisionMs !== null ? ` by ${precisionMs}ms` : ''}.
                One attempt per day — come back tomorrow!
              </p>
            )}
          </Card>
          <div style={{ height: 14 }} />
          <PrimaryButton disabled>COME BACK TOMORROW</PrimaryButton>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Pill tone="muted">Timing verified server-side · No retries</Pill>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StopClockGame;
